import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, ClipboardList, Kanban, Users, FileText, BarChart3,
  Inbox, Settings, LogOut, Menu, X, Plus, Moon, Sun, Handshake, Package, ListTodo, Sliders, ScanLine, Boxes, Clock, Truck, Award } from "lucide-react";

const navGroups = [
  { title: null, links: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", perm: "dashboard" },
  ]},
  { title: "PRINTING", links: [
    { to: "/queue", label: "My Queue", icon: ListTodo, testid: "nav-queue", perm: "queue" },
    { to: "/jobs", label: "All Job", icon: ClipboardList, testid: "nav-jobs", perm: "jobs" },
    { to: "/production", label: "Production Board", icon: Kanban, testid: "nav-production", perm: "production" },
    { to: "/scan", label: "Bulk Scan", icon: ScanLine, testid: "nav-scan", perm: "queue" },
  ]},
  { title: "SALES & STORE", links: [
    { to: "/enquiries", label: "Enquiries", icon: Inbox, testid: "nav-enquiries", perm: "enquiries" },
  ]},
  { title: "PEOPLE", links: [
    { to: "/settings", label: "Rozfa Staff", icon: Settings, testid: "nav-settings", perm: "settings" },
    { to: "/agents", label: "Rozfa Agents", icon: Handshake, testid: "nav-agents", perm: "agents" },
    { to: "/customers", label: "Customer", icon: Users, testid: "nav-customers", perm: "customers" },
    { to: "/attendance", label: "Attendance", icon: Clock, testid: "nav-attendance", always: true },
    { to: "/awards", label: "Award", icon: Award, testid: "nav-awards", always: true },
  ]},
  { title: "STOCK & IMPORT", links: [
    { to: "/products", label: "Product", icon: Package, testid: "nav-products", perm: "products" },
    { to: "/stock", label: "Stock", icon: Boxes, testid: "nav-stock", perm: "stock" },
    { to: "/outsource", label: "Plate/Stamping Order", icon: Truck, testid: "nav-outsource", perm: "stock" },
  ]},
  { title: null, divider: true, links: [
    { to: "/reports", label: "Report", icon: BarChart3, testid: "nav-reports", perm: "reports" },
    { to: "/system", label: "System Settings", icon: Sliders, testid: "nav-system", perm: "system" },
  ]},
];

export default function Layout() {
  const { user, logout, hasPerm } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [pendingAgents, setPendingAgents] = useState(0);
  const [outsourceNotOrder, setOutsourceNotOrder] = useState(0);
  const [dark, setDark] = useState(() => document.documentElement.classList.contains("dark"));

  useEffect(() => {
    const load = () => {
      api.get("/users").then((r) => setPendingAgents(r.data.filter((u) => u.role === "agent" && !u.approved).length)).catch(() => {});
      if (hasPerm("stock")) {
        api.get("/outsource-orders").then((r) => setOutsourceNotOrder(r.data.reduce((n, o) => n + (o.components || []).filter((c) => c.status === "not_order").length, 0))).catch(() => {});
      }
    };
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  const toggleDark = () => {
    const el = document.documentElement;
    el.classList.toggle("dark");
    setDark(el.classList.contains("dark"));
  };

  const handleLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside
        className={`${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 fixed lg:sticky top-0 left-0 z-40 w-64 h-screen bg-slate-900 dark:bg-slate-950 text-slate-100 flex flex-col transition-transform`}
        data-testid="sidebar"
      >
        <div className="px-5 py-5 border-b border-slate-800">
          <img src="/rozfa-logo-white.png" alt="Rozfa Print Ops" className="h-9 w-auto" data-testid="app-logo" />
          <div className="text-[10px] text-slate-400 font-mono mt-1.5">Job Management</div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {navGroups.map((group, gi) => {
            const groupLinks = group.links.filter((l) => l.always || hasPerm(l.perm));
            if (groupLinks.length === 0) return null;
            return (
              <div key={gi} className={`${group.title ? "pt-4 first:pt-0" : ""} ${group.divider ? "mt-4 pt-4 border-t border-slate-800" : ""}`}>
                {group.title && (
                  <div className="px-3 pb-1 text-[11px] font-bold uppercase tracking-wider text-slate-500 select-none" data-testid={`nav-group-${group.title.replace(/[^a-z]+/gi, "-").toLowerCase()}`}>
                    {group.title}
                  </div>
                )}
                {groupLinks.map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    onClick={() => setOpen(false)}
                    data-testid={l.testid}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        isActive ? "bg-cyan-500/20 text-cyan-300" : "text-slate-300 hover:bg-slate-800 hover:text-white"
                      }`
                    }
                  >
                    <l.icon className="w-4 h-4" />
                    {l.label}
                    {l.to === "/agents" && pendingAgents > 0 && (
                      <span className="ml-auto min-w-5 h-5 px-1.5 rounded-full bg-amber-400 text-slate-950 text-[11px] font-bold flex items-center justify-center" data-testid="nav-agents-pending-badge">{pendingAgents}</span>
                    )}
                    {l.to === "/outsource" && outsourceNotOrder > 0 && (
                      <span className="ml-auto min-w-5 h-5 px-1.5 rounded-full bg-rose-500 text-white text-[11px] font-bold flex items-center justify-center" data-testid="nav-outsource-badge">{outsourceNotOrder}</span>
                    )}
                  </NavLink>
                ))}
              </div>
            );
          })}
        </nav>

        <div className="px-3 py-3 border-t border-slate-800">
          <div className="px-3 py-2 mb-2">
            <div className="text-xs text-slate-400">Signed in as</div>
            <div className="text-sm font-medium truncate">{user?.name}</div>
            <div className="text-xs text-slate-500 font-mono">{user?.role}</div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-slate-300 hover:bg-slate-800 hover:text-white transition"
          >
            <LogOut className="w-4 h-4" /> Log out
          </button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-20 bg-background/90 backdrop-blur border-b border-border px-4 lg:px-8 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="menu-toggle">
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <div className="hidden md:block">
              <div className="text-xs text-muted-foreground font-mono">Operations Console</div>
              <div className="font-display font-semibold">ROZFA GROUP SDN BHD</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline" size="sm" onClick={toggleDark}
              data-testid="theme-toggle"
            >
              {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            {hasPerm("sales") && (
              <Button onClick={() => nav("/jobs/new")} data-testid="new-order-btn" className="bg-cyan-600 hover:bg-cyan-700 text-white">
                <Plus className="w-4 h-4 mr-1" /> New Order
              </Button>
            )}
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-8 overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
